import React, { useMemo } from 'react';
import { ChevronRight, Users } from 'lucide-react';
import { resolveProfilePic, handleProfilePicError } from '../lib/avatar';

const statusConfig = {
  active: {
    label: 'Active',
    pill: 'bg-green-500 text-white',
    tab: 'bg-green-500 text-white',
  },
  inactive: {
    label: 'Inactive',
    pill: 'bg-gray-100 text-gray-500',
    tab: 'bg-gray-400 text-white',
  },
};

const formatCount = (value) => Number(value || 0);

const formatJoinedAt = (value) => {
  if (!value) return 'Recently joined';
  try {
    return `Joined ${new Date(value).toLocaleDateString()}`;
  } catch {
    return 'Recently joined';
  }
};

const ReferralListPanel = ({
  items = [],
  summary = {},
  referralTab = 'active',
  onChangeTab,
  isLoading = false,
  error = '',
  previewLimit = null,
  onViewFullList,
  onReferralClick,
  isFullPage = false,
}) => {
  const normalizedItems = useMemo(() => items.map((item) => ({
    ...item,
    status: item.status === 'active' ? 'active' : 'inactive',
  })), [items]);
  const normalizedSummary = useMemo(() => {
    const total = Number(summary.total ?? normalizedItems.length);
    const active = normalizedItems.filter((item) => item.status === 'active').length;
    const inactive = normalizedItems.filter((item) => item.status === 'inactive').length;

    return { active, inactive, total };
  }, [normalizedItems, summary]);
  const filteredItems = useMemo(() => normalizedItems.filter((item) => item.status === referralTab), [normalizedItems, referralTab]);
  const visibleItems = useMemo(() => {
    if (!previewLimit) return filteredItems;
    return filteredItems.slice(0, previewLimit);
  }, [filteredItems, previewLimit]);

  const totalCount = formatCount(normalizedSummary.total);

  return (
    <div className="space-y-4">
      <div className="glass-panel rounded-[18px] p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-[#000000] font-['Outfit'] flex items-center gap-2">
              <Users className="w-4 h-4 text-[#9333EA]" />
              Referral List
            </p>
            <p className="text-xs text-[#666666] font-['DM_Sans'] mt-1">
              {isFullPage ? 'See every referral clearly on one screen.' : 'Preview your latest referrals here.'}
            </p>
          </div>
          {!isFullPage && typeof onViewFullList === 'function' && (
            <button
              type="button"
              onClick={onViewFullList}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-[#9333EA]/20"
            >
              View Full List
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-[14px] bg-white/70 p-3 text-center">
            <p className="text-xl font-bold text-green-600 font-['Outfit']">{formatCount(normalizedSummary.active)}</p>
            <p className="text-[10px] text-[#666666] font-['DM_Sans']">Active</p>
          </div>
          <div className="rounded-[14px] bg-white/70 p-3 text-center">
            <p className="text-xl font-bold text-gray-400 font-['Outfit']">{formatCount(normalizedSummary.inactive)}</p>
            <p className="text-[10px] text-[#666666] font-['DM_Sans']">Inactive</p>
          </div>
          <div className="rounded-[14px] bg-white/70 p-3 text-center">
            <p className="text-xl font-bold text-[#000000] font-['Outfit']">{totalCount}</p>
            <p className="text-[10px] text-[#666666] font-['DM_Sans']">Total</p>
          </div>
        </div>

        <div className="flex gap-2">
          {['active', 'inactive'].map((tabKey) => {
            const active = referralTab === tabKey;
            const config = statusConfig[tabKey];
            return (
              <button
                key={tabKey}
                type="button"
                onClick={() => onChangeTab(tabKey)}
                className={`flex-1 rounded-full px-3 py-2.5 text-xs font-semibold transition-colors ${active ? config.tab : 'glass-panel text-[#666666]'}`}
              >
                {config.label} ({formatCount(normalizedSummary[tabKey])})
              </button>
            );
          })}
        </div>
      </div>

      <div className={`space-y-2.5 ${isFullPage ? '' : 'max-h-[360px] overflow-y-auto pr-1'}`}>
        {isLoading ? (
          <div className="glass-panel rounded-[16px] p-5 text-sm text-[#666666] font-['DM_Sans']">
            Loading referrals...
          </div>
        ) : error ? (
          <div className="glass-panel rounded-[16px] p-5 text-sm text-red-600 font-['DM_Sans']">
            {error}
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="glass-panel rounded-[16px] p-5 text-sm text-[#666666] font-['DM_Sans']">
            No referrals found in this tab yet.
          </div>
        ) : (
          visibleItems.map((referral) => {
            const config = statusConfig[referral.status] || statusConfig.inactive;
            const credits = Number(referral.credits || 0);
            const hasCredits = credits > 0;

            // Referral activation reward progress (backend-derived). Referrals from
            // before the programme launched are not eligible and show nothing.
            const qualifyingDaysRequired = Number(referral.qualifyingDaysRequired || 0);
            const qualifyingDays = Number(referral.qualifyingDays || 0);
            const activationRewarded = Boolean(referral.activationRewardCredited);
            const activationRewardAmount = Number(referral.activationRewardAmount || 0);
            const showActivationProgress = Boolean(referral.activationRewardEligible) && qualifyingDaysRequired > 0;

            const referralUserId = referral.userId || referral.id;
            const canOpenProfile = Boolean(referralUserId && typeof onReferralClick === 'function');

            return (
              <button
                key={referral.id}
                type="button"
                onClick={() => canOpenProfile && onReferralClick(referral)}
                disabled={!canOpenProfile}
                aria-label={canOpenProfile ? `View ${referral.name || referral.username || 'referral'}'s profile` : undefined}
                className={`glass-panel w-full rounded-[16px] p-3 text-left transition ${canOpenProfile ? 'cursor-pointer hover:scale-[1.01] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#9333EA]/40 active:scale-[0.99]' : 'cursor-default'}`}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={resolveProfilePic(referral.profilePic)}
                    alt={referral.name}
                    onError={handleProfilePicError}
                    className="h-11 w-11 shrink-0 rounded-full object-cover border border-white/70"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <p className="truncate text-sm font-semibold text-[#000000] font-['Outfit'] sm:text-base">
                        {referral.name}
                      </p>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${config.pill}`}>
                        {config.label}
                      </span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-['DM_Sans']">
                      <span className="text-[#666666]">{referral.username}</span>
                      <span className="text-[#B0B0B0]">•</span>
                      <span className="text-[#8A8A8A]">{formatJoinedAt(referral.createdAt)}</span>
                    </div>
                    {showActivationProgress && (
                      activationRewarded ? (
                        <span
                          data-testid="referral-activation-rewarded"
                          className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 font-['DM_Sans']"
                        >
                          ₹{activationRewardAmount} reward earned
                        </span>
                      ) : (
                        <span
                          data-testid="referral-activation-progress"
                          className="mt-1 inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-semibold text-[#8A8A8A] font-['DM_Sans']"
                        >
                          {qualifyingDays}/{qualifyingDaysRequired} qualifying days
                        </span>
                      )
                    )}
                  </div>
                  {hasCredits && (
                    <div className="shrink-0 text-right">
                      <p className="text-base font-bold text-green-600 font-['Outfit']">{credits}</p>
                      <p className="text-[10px] text-[#666666] font-['DM_Sans']">Credits</p>
                    </div>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {!isFullPage && previewLimit && filteredItems.length > previewLimit && (
        <p className="text-center text-xs text-[#666666] font-['DM_Sans']">
          Showing {visibleItems.length} of {filteredItems.length} referrals in this tab.
        </p>
      )}
    </div>
  );
};

export default ReferralListPanel;
